import React, { useState } from "react";
import { Button, Card, Radio, Form, Input, Tag } from "antd";
import { CSVLink } from "react-csv";
import { doGet } from "../../../../../API/apis";
import { formatDate } from "../../../../../utils/DateFormat";
import FormatDate from "../../../../../components/FormatDate/FormatDate";
import CustomTable from "../../../../../components/CustomTable/CustomTable";
import Notification from "../../../../../components/Notification/Notification";
import { DetailCard } from "../../../../../components/GlobalComponents/GlobalComponents";

const { Group: RadioGroup } = Radio;

const Referral = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchCategory, setSearchCategory] = useState("referal_full_name");
  
  // Create dynamic columns based on search category
  const getColumns = () => [
    {
      key: "1",
      title: "ID",
      dataIndex: "id",
      align: "center",
    },
    {
      key: "patient_mrn",
      title: "MRN",
      dataIndex: "mrn_number",
    },
    {
      key: "2",
      title: "First Name",
      dataIndex: "first_name",
      ellipsis: true,
    },
    {
      key: "3",
      title: "Middle Name",
      dataIndex: "middle_name",
      ellipsis: true,
    },
    {
      key: "4",
      title: "Last Name",
      dataIndex: "last_name",
      ellipsis: true,
    },
    {
      key: "5",
      title: "DOB",
      dataIndex: "date_of_birth",
      ellipsis: true,
      render: (text) => <FormatDate date={text}/>,
    },
    {
      key: "6",
      title: "Gender",
      ellipsis: true,
      dataIndex: "gender",
    },
    {
      key: "7",
      title: "Marital Status",
      ellipsis: true,
      dataIndex: "marital_status",
    },
    {
      key: 8,
      title: "Source",
      dataIndex: "referral_source",
      render: (text, record) => {
        // Show data based on the selected search category
        switch (searchCategory) {
          case "referal_full_name":
            return record.referal_full_name || "-";
          case "referal_organization_name":
            return record.referal_organization_name || "-";
          case "referal_group_name":
            return record.referal_group_name || "-";
          case "wdyhau":
            return record.wdyhau || "-";
          default:
            return "-";
        }
      }
    },
    {
      key:"9",
      title:"Reg. Date",
      dataIndex:"registration_date",
      render: (text) => <FormatDate date={text} />,
    }
  ];

  const handleSearch = async () => {
    const searchParam = form.getFieldValue(searchCategory);
    setLoading(true);

    try {
      const response = await doGet(
        `/reports/referal-reports/?filter_field=${searchCategory}&q=${searchParam}`
      );
      if (response.status === 200) {
        setData(response.data);
      } else if(response.status === 400){
        Notification.error("No Record Found")
        setData([])
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Update CSV export function to use selected category
  const transformHealthRecord = (records) => {
    if (!records || !Array.isArray(records)) {
      return [];
    }

    return records.map((record) => {
      // Determine the referral source for CSV export based on selected category
      let referralSource = "-";
      switch (searchCategory) {
        case "referal_full_name":
          referralSource = record.referal_full_name || "-";
          break;
        case "referal_organization_name":
          referralSource = record.referal_organization_name || "-";
          break;
        case "referal_group_name":
          referralSource = record.referal_group_name || "-";
          break;
        case "wdyhau":
          referralSource = record.wdyhau || "-";
          break;
        default:
          referralSource = "-";
      }

      return {
        ID: record.id,
        MRN: record.mrn_number,
        "First Name": record?.first_name,
        "Middle Name": record?.middle_name,
        "Last Name": record?.last_name,
        DOB: formatDate(record?.date_of_birth),
        Gender: record?.gender,
        MaritalStatus: record?.marital_status,
        ReferralSource: referralSource,
        "Referral Notes": record?.referral_notes || "-"
      };
    });
  };

  return (
    <>
      <DetailCard title="Referral Report">
        <h1> Search By Category </h1>
        <RadioGroup
          onChange={(e) => setSearchCategory(e.target.value)}
          value={searchCategory}
          buttonStyle="solid"
          className="mb-2 mt-2"
        >
          <Radio.Button value="referal_full_name">Referral Doctor</Radio.Button>
          <Radio.Button value="referal_organization_name">
            Referral Organization
          </Radio.Button>
          <Radio.Button value="referal_group_name">Referral Group</Radio.Button>
          <Radio.Button value="wdyhau">Referral Source</Radio.Button>
        </RadioGroup>
        <Form layout="vertical" form={form}>
          <div className="flex items-center">
            <Form.Item
              name={searchCategory}
              label={
                searchCategory === "referal_full_name"
                  ? "Referral Doctor"
                  : searchCategory === "referal_organization_name"
                  ? "Referral Organization"
                  : searchCategory === "referal_group_name"
                  ? "Referral Group"
                  : "Referral Source"
              }
            >
              <Input
                allowClear={true}
                placeholder={`Search ${
                  searchCategory === "referal_full_name"
                    ? "Referral Doctor"
                    : searchCategory === "referal_organization_name"
                    ? "Referral Organization"
                    : searchCategory === "referal_group_name"
                    ? "Referral Group"
                    : "Referral Source"
                }`}
              />
            </Form.Item>
            <Button className="ml-2 mt-2" type="primary" onClick={handleSearch}>
              Submit
            </Button>
            <Button className="ml-2 mt-2" onClick={()=> setData([])}>
              Reset
            </Button>
          </div>
        </Form>
      </DetailCard>
      <Card className="shadow-class mt-4">
        <div className="flex justify-between items-center mb-2">
          <h1>Search Results<Tag className="ml-2 bg-green-600 text-white"> {form.getFieldValue(searchCategory)}</Tag></h1>  
          {data?.length > 0 && (
            <Button type="primary">
              <CSVLink
                data={transformHealthRecord(data)}
                filename={"PatientHealthRecord.csv"}
                className="csv-link"
              >
                Download Report
              </CSVLink>
            </Button>
          )}
        </div>
        {data && (
          <CustomTable
            scroll={{
              x: 1000,
            }}
            loading={loading}
            columns={getColumns()}
            dataSource={data}
            key="referral_reports"
          />
        )}
      </Card>
    </>
  );
};

export default Referral;
